import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Sort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { forkJoin, from, Observable, of, Subscription } from 'rxjs';
import { catchError, concatMap, map, toArray } from 'rxjs/operators';
import { CookieService } from 'ngx-cookie-service';
import { SseService } from 'src/app/services/SseService';
import { SuscripcionesService } from 'src/app/services/suscripciones.service';
import { BrokerService } from 'src/app/services/broker.services';
import { CarteraOptimaParamsService } from 'src/app/services/cartera-optima-params.service';
import { MetaApiService } from 'src/app/services/meta-api.service';
import { BrokerTimeService } from 'src/app/services/broker-time.service';
import { BrokerAdapterFactory } from 'src/app/services/broker-adapter-factory.service';
import { OAuthService } from 'src/app/services/oauth.service';
import { ToastrService } from 'ngx-toastr';
import { operationinterface } from 'src/app/interface/OperationInterface';
import { OptimalWallet } from 'src/app/interface/OptimalWallet';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../common/confirm-dialog/confirm-dialog.component';
import { formatPeriodo } from 'src/app/utils/periodo.util';
import { contadorIsooper, formarIsooper } from 'src/app/shared/utils/isooper.util';

interface SubcartOption {
  idModelo: string;
  nombre: string;
  idCarteraOptima: string;
  idSubcartera: string; // id real de subcartera (idsubcarterascartera / tcc.idcartera), '' para TODOS/CARTERA_ACTUAL
}

interface ComparisonRow extends operationinterface {
  missing: boolean;
  // Apertura cuyo isooper ya se abrió y cerró en esta cartera (fin de día, tabla Posiciones, SL/TP,
  // salida del modelo): el backend la descarta por duplicada, así que se envía con el siguiente
  // contador real (broker/contador), p. ej. AAPL01 -> AAPL02.
  reentrada?: boolean;
  reentradaIsooper?: string;
  // Cierre de la posición por la OCO del broker (SL/TP), que salta a mitad de barra.
  cierreBroker?: CierreSlTpBroker;
  // El cierre es posterior a la última barra que vio el modelo: hasta que la consolide, el modelo
  // sigue "entrado" y la reentrada sería falsa, así que no se puede seleccionar.
  pendienteBarra?: boolean;
}

interface CierreSlTpBroker {
  tipo: 'SL' | 'TP';
  /** Hora de ejecución en el broker (epoch ms). */
  ts: number;
}

interface RiesgoParams {
  riesgo: number;
  tiporiesgo: string;
}

@Component({
  selector: 'app-sincronizar-comparativa-dialog',
  templateUrl: './sincronizar-comparativa-dialog.component.html',
  styleUrl: './sincronizar-comparativa-dialog.component.scss'
})
export class SincronizarComparativaDialogComponent implements OnInit, OnDestroy {
  @Output() sincronizarOperaciones = new EventEmitter<operationinterface[]>();

  // Sentinel del selector para "todas las subcarteras a la vez". Es texto (no '0') para no
  // chocar con ningún idModelo real, que siempre son ids de tbl_cartera_modelo.
  readonly TODOS_ID = 'TODOS';
  readonly CARTERA_ACTUAL_ID = 'CARTERA_ACTUAL';

  // Mismo valor que defaultOptima.tiporiesgo/riesgo en diversificador.component.ts: comparten el
  // mismo backend (RecalcularOperacion/TipoRiesgo), así que se evita introducir un default divergente.
  private readonly DEFAULT_TIPORIESGO = 'LIMITADO';
  private readonly DEFAULT_RIESGO = 1000;

  // Panel de ayuda del encabezado (icono "?"), cerrado por defecto.
  mostrarAyuda = false;
  isMinimized = false;

  loadingSuscripciones = false;
  loadingModelOps = false;
  loadingBrokerOps = false;
  loadingParams = false;
  loadingContractSpecs = false;
  loadingHistorial = false;
  errorMsg = '';

  listaSuscripciones: any[] = [];
  // Opciones que se muestran en el desplegable. Actualmente solo "Posiciones Cartera Suscrita".
  subcartOptions: SubcartOption[] = [];
  // Subcarteras/modelos reales de la suscripción. Ya no se ofrecen en el desplegable, pero se
  // siguen parseando porque cargarComparativaCarteraActual necesita el idCarteraOptima de cada
  // subcartera para resolver su riesgo/tiporiesgo antes de llamar a Recalcular.
  private subcartMeta: SubcartOption[] = [];
  selectedIdModelo: string | null = null;
  fecharegistrosuscripcion = '';
  // Datos de la suscripción que se muestran junto al selector Vista (un periodo/sentido por subcartera).
  private brokerOffsetMin: number | null = null;
  infoSuscripcion: { nombre: string; fecha: string; modelos: { nombre: string; periodo: string; bajista: boolean }[] } | null = null;

  modelRows: operationinterface[] = [];
  brokerRows: operationinterface[] = [];
  comparisonRows: ComparisonRow[] = [];

  // Parámetros de riesgo de la cartera óptima (idcarteraoptima) del subcart seleccionado.
  // Necesarios para calcular cantidadacciones igual que RecalcularOperacion en el backend
  // (apiController.java) — sin esto no se puede dimensionar de forma segura una orden real.
  walletRiesgoParams: RiesgoParams | null = null;
  sinParametrosRiesgo = false;

  // Modo "Todos": un set de parámetros de riesgo por idModelo, porque cada subcartera puede
  // tener su propia cartera óptima con su propio riesgo/tiporiesgo.
  riesgoParamsPorModelo: Record<string, RiesgoParams | null> = {};
  // SL/Profit activos de cada modelo según tbl_modelo (lo mismo que usa revisarModeloBrokerIajava).
  // Las posiciones de Recalcular traen stoplossactivo/profitactivo siempre a false.
  private flagsOcoPorModelo = new Map<number, Pick<operationinterface, 'stoplossactivo' | 'profitactivo'>>();

  // contract_size/min_volume/volume_step por idnectum (POST listadecontractsize), necesarios porque
  // las operaciones "ope" de ia/crearmodelojava (cuando el modelo ya está calculado y solo se cargan
  // de tbl_operacion vía ModeloServiceImpl.obteneroperaciones) NO traen contractSize propio — sin esto
  // el tamaño calculado puede quedar fuera de escala (p. ej. contractSize=1 en vez de 100000 en forex).
  private contractSpecs: Record<number, { contractSize: number, minVolume: number, volumeStep: number }> = {};
  // Respaldo en vivo a MetaAPI ya intentado para el subcart actual (evita reintentar en bucle).
  private contractSpecsFallbackAttempted = false;
  // Respaldo en vivo a IBKR (conid) ya intentado para el subcart actual (evita reintentar en bucle).
  private conidFallbackAttempted = false;

  selection = new SelectionModel<ComparisonRow>(true, []);

  private sseSubscription: Subscription | null = null;
  // Suscripciones SSE en vuelo del modo "Todos" (una por subcartera); se cancelan al cambiar
  // de selección o al destruir el diálogo, igual que sseSubscription para el modo de un solo modelo.
  private todosSseSubscriptions: Subscription[] = [];
  // Suscripción viva a valBrokValPos$ en modo "Cartera actual" (cargarComparativaCarteraActual);
  // se cancela al cambiar de selección o al destruir el diálogo, igual que las demás.
  private brokerPosSubscription: Subscription | null = null;

  // isooper (en mayúsculas) con ENTRADA y SALIDA en el historial real de la cartera (operaciones/{idcartera}).
  private isoopersCerrados = new Set<string>();
  // Salidas por SL (STP) o TP (LMT) del broker, por ISOOPER|A/B.
  private cierresSlTpBroker = new Map<string, CierreSlTpBroker>();
  // Siguiente isooper real por idNectum según broker/contador; se pide una sola vez por instrumento.
  private contadorPorIdNectum = new Map<number, string>();
  private contadoresPedidos = new Set<number>();
  /** Iso de cada idNectum, para separar el contador del isooper (ver isooper.util). */
  private isoPorIdNectum = new Map<number, string>();

  constructor(
    public dialogRef: MatDialogRef<SincronizarComparativaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { optimalWallet: OptimalWallet, idcarterasuscrita: number, licencia: string, isDark: boolean, getOco?: () => boolean, setOco?: (oco: boolean) => void },
    private sseService: SseService,
    private suscripcionesService: SuscripcionesService,
    private toastr: ToastrService,
    private dialog: MatDialog,
    private cookieService: CookieService,
    private metaApiService: MetaApiService,
    private brokerAdapterFactory: BrokerAdapterFactory,
    private oauthService: OAuthService,
    private brokerService: BrokerService,
    private carteraOptimaParamsService: CarteraOptimaParamsService,
    private brokerTimeService: BrokerTimeService
  ) { }

  // Getter (not a cached field): re-evaluated on every change detection cycle so
  // toggling automation in the "Automatizado" tab while this dialog is already
  // open is reflected immediately, without needing to close/reopen the dialog.
  // Sync requires automation to be STOPPED: with it running, the broker's send
  // loop would race against the queued orders (conflicts / double sends).
  get automatizacionActiva(): boolean {
    return this.cookieService.check('auto' + this.data.optimalWallet.idcartera);
  }

  get cargandoComparativa(): boolean {
    return this.loadingModelOps || this.loadingBrokerOps || this.loadingParams || this.loadingContractSpecs || this.loadingHistorial;
  }

  get displayedColumns(): string[] {
    const base = ['contador', 'select', 'isooper', 'nombreCompania', 'tipoOperacion', 'fechaOperacion',
      'precioentradareal', 'preciosalidareal', 'stoploss', 'profit', 'cantidadacciones', 'beneficioperdida', 'origen'];
    if (this.selectedIdModelo === this.TODOS_ID) {
      return ['contador', 'select', 'subcartera', ...base.slice(2)];
    }
    return base;
  }

  // Modo "Todos": aviso de riesgo parcial cuando alguna (no todas) subcartera no tiene
  // parámetros de riesgo resueltos. Sus filas faltantes ya quedan no-seleccionables solas
  // (calcularCantidadAcciones devuelve 0 sin parámetros), esto es solo el mensaje informativo.
  get algunasSinParametrosRiesgo(): boolean {
    if (this.selectedIdModelo !== this.TODOS_ID) return false;
    const valores = Object.values(this.riesgoParamsPorModelo);
    return valores.length > 0 && valores.some(v => v === null);
  }

  ngOnInit(): void {
    this.cargarSuscripciones();
    const wallet: any = this.data.optimalWallet;
    if (wallet.tipoBroker === 'METAAPI') {
      this.brokerTimeService.getOffsetMinutes(wallet.metaApiAccountId || wallet.nombre)
        .subscribe(offsetMin => this.brokerOffsetMin = offsetMin);
    }
  }

  /** Timestamp (número en s o ms, o fecha) a epoch ms; 0 si no es válido. */
  private aMs(valor: any): number {
    if (valor === null || valor === undefined || valor === '') return 0;
    const n = Number(valor);
    if (Number.isFinite(n)) return n <= 0 ? 0 : n < 1e12 ? n * 1000 : n;
    const t = new Date(valor).getTime();
    return isNaN(t) ? 0 : t;
  }

  tooltipReentrada(row: ComparisonRow): string {
    const cierre = row.cierreBroker;
    const porBroker = cierre
      ? ' El broker la cerró por ' + cierre.tipo + ' a las ' + this.brokerTimeService.formatTime(new Date(cierre.ts), this.brokerOffsetMin) + '.'
      : '';
    if (row.pendienteBarra) {
      return (porBroker + ' El modelo lo confirmará al cerrar la barra; hasta entonces no se puede reentrar.').trim();
    }
    return row.isooper + ' ya se cerró en esta cartera.' + porBroker + ' Se enviará como nueva operación '
      + (row.reentradaIsooper || '(obteniendo ID…)') + '.';
  }

  tooltipNoSeleccionable(row: ComparisonRow): string {
    if (row.pendienteBarra) return 'Pendiente del cierre de barra: el broker la cerró por ' + row.cierreBroker?.tipo;
    if (row.reentrada && !row.reentradaIsooper) return 'Sin ID nuevo para la reentrada';
    return 'No se pudo calcular un tamaño de posición válido';
  }

  /**
   * Hora de una fila cuya fecha no la lleva (las posiciones del broker traen solo yyyy-MM-dd).
   * Sale de `hora`, en hora del broker, para poder compararla con la de la operación.
   */
  horaDeFila(row: operationinterface): string {
    const hora = Number(row.hora);
    if (!hora) return '';
    const fecha = row.fechaOperacion as any;
    const soloFecha = typeof fecha === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(fecha);
    const d = new Date(fecha);
    if (!soloFecha && (isNaN(d.getTime()) || d.getHours() !== 0 || d.getMinutes() !== 0 || d.getSeconds() !== 0)) return '';
    return this.brokerTimeService.formatTime(new Date(hora * 1000), this.brokerOffsetMin);
  }

  ngOnDestroy(): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
    if (this.brokerPosSubscription) {
      this.brokerPosSubscription.unsubscribe();
    }
    this.cancelarCargasTodos();
  }

  private cancelarCargasTodos(): void {
    this.todosSseSubscriptions.forEach(s => s.unsubscribe());
    this.todosSseSubscriptions = [];
  }

  cargarSuscripciones(): void {
    this.loadingSuscripciones = true;
    this.errorMsg = '';
    this.suscripcionesService.listarSuscripcionesConfirmar('').subscribe({
      next: (value: any[]) => {
        this.listaSuscripciones = (value || []).filter((s: any) => s.idcartera === this.data.idcarterasuscrita);
        this.loadingSuscripciones = false;
        if (this.listaSuscripciones.length === 0) {
          this.errorMsg = 'No se encontró ninguna suscripción para esta cartera.';
          return;
        }
        this.construirSubcartOptions();
      },
      error: () => {
        this.loadingSuscripciones = false;
        this.errorMsg = 'Error al consultar las suscripciones.';
      }
    });
  }

  // El desplegable ya no ofrece los modelos/subcarteras ni el modo "Todos": la única vista es
  // "Posiciones Cartera Suscrita" (CARTERA_ACTUAL_ID), que queda seleccionada por defecto.
  // Las subcarteras se siguen parseando en subcartMeta (no en subcartOptions) porque
  // cargarComparativaCarteraActual las necesita para resolver el idCarteraOptima de cada una.
  private construirSubcartOptions(): void {
    const suscripcion = this.listaSuscripciones[0];
    this.fecharegistrosuscripcion = suscripcion.fechasuscripcion;
    this.construirInfoSuscripcion(suscripcion);

    this.subcartMeta = [];
    if (suscripcion.idsubcarteras && suscripcion.subcarteras) {
      const ids = String(suscripcion.idsubcarteras).split(',');
      const nombres = String(suscripcion.subcarteras).split(',');
      const opts = suscripcion.idcarteraoptima ? String(suscripcion.idcarteraoptima).split(',') : [];
      const subIds = suscripcion.idsubcarterascartera ? String(suscripcion.idsubcarterascartera).split(',') : [];
      const n = Math.min(ids.length, nombres.length, opts.length || ids.length, subIds.length || ids.length);
      for (let i = 0; i < n; i++) {
        if (ids[i] === undefined || nombres[i] === undefined) continue;
        this.subcartMeta.push({
          idModelo: ids[i].trim(),
          nombre: nombres[i].trim(),
          idCarteraOptima: (opts[i] ?? '').trim(),
          idSubcartera: (subIds[i] ?? '').trim()
        });
      }
    } else if (suscripcion.idmodelo) {
      this.subcartMeta = [{
        idModelo: String(suscripcion.idmodelo),
        nombre: suscripcion.nombre ?? String(suscripcion.idmodelo),
        idCarteraOptima: suscripcion.idcarteraoptima ? String(suscripcion.idcarteraoptima) : '',
        idSubcartera: suscripcion.idsubcarterascartera
          ? String(suscripcion.idsubcarterascartera).trim()
          : String(this.data.idcarterasuscrita)
      }];
    }

    this.subcartOptions = [
      { idModelo: this.CARTERA_ACTUAL_ID, nombre: 'Posiciones Cartera Suscrita', idCarteraOptima: '', idSubcartera: '' }
    ];
    this.seleccionarSubcart(this.CARTERA_ACTUAL_ID);
  }

  // subcarteras, periodo y bajista llegan como CSV con un valor por subcartera, en el mismo orden
  // (ver SuscripcionDaoImpl.listarSuscripciones); fechasuscripcion es texto 'yyyy-MM-dd HH:mm:ss...'.
  private construirInfoSuscripcion(suscripcion: any): void {
    // nombre = cartera a la que se suscribió (lo mismo que "Cart." en SuscripcionesConfirmarComponent);
    // nombresuscripcion es el nombre de la cuenta de broker, que ya sale en el título.
    const nombreCartera: string = suscripcion.nombre || '';
    const nombres = suscripcion.subcarteras ? String(suscripcion.subcarteras).split(',') : [];
    const periodos = suscripcion.periodo ? String(suscripcion.periodo).split(',') : [];
    const bajistas = suscripcion.bajista ? String(suscripcion.bajista).split(',') : [];
    this.infoSuscripcion = {
      nombre: nombreCartera,
      fecha: suscripcion.fechasuscripcion ? String(suscripcion.fechasuscripcion).substring(0, 16) : '',
      modelos: periodos.map((p, i) => {
        const nombre = (nombres[i] ?? '').trim();
        return {
          // Sin subcarteras el backend rellena subcarteras con el nombre de la cartera: no repetirlo.
          nombre: nombre !== nombreCartera ? nombre : '',
          periodo: formatPeriodo(p.trim()),
          bajista: (bajistas[i] ?? '').trim() === 'true'
        };
      })
    };
  }

  seleccionarSubcart(idModelo: string): void {
    this.selectedIdModelo = idModelo;
    this.comparisonRows = [];
    this.walletRiesgoParams = null;
    this.riesgoParamsPorModelo = {};
    this.sinParametrosRiesgo = false;
    this.contractSpecsFallbackAttempted = false;
    this.conidFallbackAttempted = false;
    this.selection.clear();
    this.cancelarCargasTodos();

    if (idModelo === this.CARTERA_ACTUAL_ID) {
      this.cargarComparativaCarteraActual();
      return;
    }

    this.cargarOperacionesBroker();

    if (idModelo === this.TODOS_ID) {
      this.cargarTodasLasSubcarteras();
    } else {
      const opt = this.subcartMeta.find(o => o.idModelo === idModelo);
      this.cargarOperacionesModelo(idModelo);
      this.cargarParametrosRiesgo(opt?.idCarteraOptima);
    }
  }

  private cargarParametrosRiesgo(idCarteraOptima: string | undefined): void {
    if (!idCarteraOptima) {
      this.sinParametrosRiesgo = true;
      this.loadingParams = false;
      this.calcularComparativa();
      return;
    }
    this.loadingParams = true;
    this.sseService.apiGetNew('parametros/' + idCarteraOptima).subscribe({
      next: (data: any) => {
        console.log('[Sincronizar][parametros/' + idCarteraOptima + '] respuesta completa:', data);
        if (data && data.riesgo != null && data.tiporiesgo) {
          this.walletRiesgoParams = { riesgo: Number(data.riesgo), tiporiesgo: String(data.tiporiesgo) };
          console.log('[Sincronizar] walletRiesgoParams resuelto:', this.walletRiesgoParams);
        } else {
          this.sinParametrosRiesgo = true;
          console.warn('[Sincronizar] parametros/' + idCarteraOptima + ' no trajo riesgo/tiporiesgo válidos');
        }
        this.loadingParams = false;
        this.calcularComparativa();
      },
      error: () => {
        this.sinParametrosRiesgo = true;
        this.loadingParams = false;
        this.calcularComparativa();
      }
    });
  }

  // Modo "Todos": misma llamada que cargarParametrosRiesgo pero como Observable de un solo
  // valor (null si no hay parámetros o falla), para poder combinarla con forkJoin por subcartera.
  private obtenerRiesgoParaSubcart(idCarteraOptima: string | undefined): Observable<RiesgoParams | null> {
    if (!idCarteraOptima) {
      return of(null);
    }
    return this.sseService.apiGetNew('parametros/' + idCarteraOptima).pipe(
      map((data: any) => (data && data.riesgo != null && data.tiporiesgo)
        ? { riesgo: Number(data.riesgo), tiporiesgo: String(data.tiporiesgo) }
        : null),
      catchError(() => of(null))
    );
  }

  // Replica la fórmula de dimensionamiento de RecalcularOperacion (apiController.java):
  // FIJO:     cantidadacciones = riesgo / (contractSize * precioEntrada)
  // LIMITADO: cantidadacciones = riesgo / (contractSize * |precioEntrada - stopLossInicial|)
  // No incluye el "gating" de margen/riesgo acumulado que hace Recalcular durante la simulación
  // completa (depende de estado de operaciones previas) — solo el tamaño "en el vacío" de esta
  // operación, que es lo máximo replicable de forma aislada para una sola orden manual.
  private calcularCantidadAcciones(row: operationinterface): number {
    const logTag = `[Sincronizar][calcularCantidadAcciones][${row.nombreCompania} ${row.tipoOperacion} isooper=${row.isooper}]`;

    // En modo "Todos" cada fila usa el riesgo de SU PROPIA subcartera (idmodelo), no un único
    // walletRiesgoParams global, porque cada subcartera puede tener su propia cartera óptima.
    const riesgoParams = this.selectedIdModelo === this.TODOS_ID
      ? this.riesgoParamsPorModelo[String(row.idmodelo)] ?? null
      : this.walletRiesgoParams;

    if (!riesgoParams) {
      console.warn(`${logTag} sin parámetros de riesgo -> 0`);
      return 0;
    }
    const { riesgo, tiporiesgo } = riesgoParams;
    const esMetaApi = this.data.optimalWallet.tipoBroker === 'METAAPI';
    const spec = this.contractSpecs[row.idNectum];

    console.log(`${logTag} inputs:`, {
      riesgo, tiporiesgo, esMetaApi,
      idNectum: row.idNectum,
      precioentradareal: row.precioentradareal,
      stoplossinicial: row.stoplossinicial,
      spec
    });

    // tbl_metaapi_symbol_specifications (y por tanto listadecontractsize/el fallback en vivo) solo
    // tiene sentido para instrumentos MetaAPI/CFD; para el resto (acciones vía IBKR) el backend usa
    // COALESCE(tmss.contract_size, 1) — replicamos ese mismo default sin depender de un spec.
    if (!esMetaApi) {
      const contractSize = 1;
      let cantidadStock: number;
      if (tiporiesgo === 'FIJO') {
        cantidadStock = riesgo / (contractSize * row.precioentradareal);
        console.log(`${logTag} FIJO no-MetaAPI: ${riesgo} / (${contractSize} * ${row.precioentradareal}) = ${cantidadStock}`);
      } else {
        const denomStock = Math.abs(row.precioentradareal - row.stoplossinicial);
        cantidadStock = denomStock > 0 ? riesgo / (contractSize * denomStock) : 0;
        console.log(`${logTag} LIMITADO no-MetaAPI: ${riesgo} / (${contractSize} * |${row.precioentradareal} - ${row.stoplossinicial}| = ${denomStock}) = ${cantidadStock}`);
      }
      if (!Number.isFinite(cantidadStock) || cantidadStock <= 0) {
        console.warn(`${logTag} resultado no finito o <= 0 -> 0`, cantidadStock);
        return 0;
      }
      if (!this.data.optimalWallet.participacion) {
        const antes = cantidadStock;
        cantidadStock = Math.floor(cantidadStock);
        console.log(`${logTag} floor (participacion=false): ${antes} -> ${cantidadStock}`);
      }
      console.log(`${logTag} RESULTADO FINAL:`, cantidadStock);
      return cantidadStock;
    }

    // MetaAPI: sin spec resuelto (ni por BD ni por el fallback en vivo) no se puede fiar el tamaño;
    // no sizear "en el vacío" con un valor por defecto que podría estar fuera de escala.
    if (!spec) {
      console.warn(`${logTag} MetaAPI sin contractSpec resuelto -> 0`);
      return 0;
    }
    const contractSize = spec.contractSize;

    let cantidad: number;
    if (tiporiesgo === 'FIJO') {
      cantidad = riesgo / (contractSize * row.precioentradareal);
      console.log(`${logTag} FIJO MetaAPI: ${riesgo} / (${contractSize} * ${row.precioentradareal}) = ${cantidad}`);
    } else {
      // LIMITADO (y fallback para MIXTO, que no es resoluble de forma aislada aquí)
      const denom = Math.abs(row.precioentradareal - row.stoplossinicial);
      cantidad = denom > 0 ? riesgo / (contractSize * denom) : 0;
      console.log(`${logTag} LIMITADO MetaAPI: ${riesgo} / (${contractSize} * |${row.precioentradareal} - ${row.stoplossinicial}| = ${denom}) = ${cantidad}`);
    }
    if (!Number.isFinite(cantidad) || cantidad <= 0) {
      console.warn(`${logTag} resultado no finito o <= 0 -> 0`, cantidad);
      return 0;
    }

    const antesDeRedondeo = cantidad;
    cantidad = Math.round(cantidad * 100) / 100;
    if (spec.minVolume > 0 && cantidad < spec.minVolume) {
      console.log(`${logTag} ${cantidad} < minVolume ${spec.minVolume} -> snap a minVolume`);
      cantidad = spec.minVolume;
    } else if (spec.volumeStep > 0) {
      const remainder = cantidad % spec.volumeStep;
      if (remainder !== 0) {
        console.log(`${logTag} ${cantidad} % volumeStep ${spec.volumeStep} = ${remainder} -> se resta el resto`);
        cantidad -= remainder;
      }
    }
    console.log(`${logTag} redondeo/snap: ${antesDeRedondeo} -> RESULTADO FINAL: ${cantidad}`);
    return cantidad;
  }

  // Construye el jsonData que espera ia/crearmodelojava a partir de la respuesta de
  // parametrosmodelo, compartido entre el modo de un solo modelo y el modo "Todos".
  private construirJsonDataModelo(data: any, condicionln: any, ln: any): any {
    const fecha = this.fecharegistrosuscripcion ? this.fecharegistrosuscripcion.split(' ')[0] : condicionln.start;
    const hora = this.fecharegistrosuscripcion ? (this.fecharegistrosuscripcion.split(' ')[1] || null) : null;

    return {
      list_conditions_entry: condicionln.list_conditions_entry,
      list_conditions_exit: condicionln.list_conditions_exit,
      start: fecha,
      hora: hora,
      end: new Date().toISOString().split('T')[0],
      idnectums: [],
      stopLoss: condicionln.stopLoss,
      profit: condicionln.profit,
      bajista: condicionln.bajista,
      periodo: data.periodo,
      idmercado: condicionln.idmercado,
      metaapi: condicionln.metaapi,
      sectype: condicionln.sectype,
      ln: ln,
      periodoModeloPeriodico: condicionln.periodoModeloPeriodico,
      nocondiciones: condicionln.nocondiciones,
      repeticionentradas: condicionln.repeticionentradas,
      stopLossActivo: condicionln.stopLossActivo,
      stopLossDinamicActivo: condicionln.stopLossDinamicActivo,
      proFitActivo: condicionln.proFitActivo,
      diasSalidaActivo: condicionln.diasSalidaActivo,
      diasSalida: condicionln.diasSalida,
      salidaFinDiaActivo: condicionln.salidaFinDiaActivo,
      licencia: this.data.licencia
    };
  }

  // ia/crearmodelojava no rellena stoplossactivo/profitactivo en cada operación (llegan a false por
  // defecto); construirOrdersData solo añade SL/TP al OCO si valen true, igual que en
  // revisarModeloBrokerIajava, así que se toman de la configuración del modelo.
  private flagsOco(jsonData: any): Pick<operationinterface, 'stoplossactivo' | 'profitactivo'> {
    return {
      stoplossactivo: jsonData.stopLossActivo === true,
      profitactivo: jsonData.proFitActivo === true
    };
  }

  private cargarOperacionesModelo(idModelo: string): void {
    this.loadingModelOps = true;
    this.errorMsg = '';
    this.modelRows = [];

    this.sseService.apiGetNew('parametrosmodelo?idmodelo=' + idModelo).subscribe({
      next: (data: any) => {
        let condicionln: any;
        try {
          condicionln = JSON.parse(data.condicionln);
        } catch {
          this.loadingModelOps = false;
          this.errorMsg = 'No se pudieron leer las condiciones del modelo.';
          return;
        }
        let ln: any;
        try {
          ln = JSON.parse(data.lnList);
        } catch {
          ln = [];
        }

        const jsonData = this.construirJsonDataModelo(data, condicionln, ln);
        this.dispararModeloSse(jsonData, idModelo);
        this.cargarContractSpecs(condicionln.idmercado);
      },
      error: () => {
        this.loadingModelOps = false;
        this.errorMsg = 'Error al obtener los parámetros del modelo.';
      }
    });
  }

  private cargarContractSpecs(idmercado: number): void {
    this.contractSpecs = {};
    if (!idmercado) {
      this.loadingContractSpecs = false;
      this.calcularComparativa();
      return;
    }
    this.loadingContractSpecs = true;
    console.log('[Sincronizar][metaapi/listadecontractsize] idmercado=' + idmercado);
    this.sseService.apiPostNew('metaapi/listadecontractsize', { idmercado }, this.data.licencia).subscribe({
      next: (data: any[]) => {
        console.log('[Sincronizar][metaapi/listadecontractsize] respuesta cruda:', data);
        (data || []).forEach((spec: any) => {
          const idnectum = Number(spec.idnectum);
          this.contractSpecs[idnectum] = {
            contractSize: Number(spec.contract_size) > 0 ? Number(spec.contract_size) : 1,
            minVolume: Number(spec.min_volume) || 0,
            volumeStep: Number(spec.volume_step) || 0
          };
        });
        console.log('[Sincronizar] contractSpecs (BD) resuelto:', this.contractSpecs);
        this.loadingContractSpecs = false;
        this.calcularComparativa();
      },
      error: () => {
        // Sin especificaciones de contrato, calcularCantidadAcciones devuelve 0 para toda
        // fila (no hay entrada en contractSpecs), así que ninguna quedará seleccionable;
        // avisamos explícitamente en vez de dejar que el usuario adivine por qué.
        this.loadingContractSpecs = false;
        this.toastr.warning('No se pudieron obtener los tamaños de contrato del mercado; revisa la cantidad calculada antes de sincronizar', 'Sincronizar');
        this.calcularComparativa();
      }
    });
  }

  private dispararModeloSse(jsonData: any, idModelo: string): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
    let ope: any[] = [];
    let recibioError = false;
    this.sseSubscription = this.sseService.apiGetSse('ia/crearmodelojava?data=', true, encodeURIComponent(JSON.stringify(jsonData))).subscribe({
      next: (resp: any) => {
        if (resp.name === 'ope') {
          ope.push(resp.data);
        } else if (resp.name === 'err') {
          recibioError = true;
          this.toastr.error(resp.data, 'Error al calcular operaciones del modelo');
        }
      },
      error: () => {
        this.loadingModelOps = false;
        this.errorMsg = 'Error de conexión al calcular las operaciones del modelo.';
      },
      complete: () => {
        // Se etiqueta explícitamente idmodelo (en vez de confiar en lo que traiga cada fila)
        // porque calcularComparativa ahora usa idmodelo como parte de la clave de comparación.
        this.modelRows = ope.flatMap((batch: any[]) => batch)
          .map((r: operationinterface) => ({ ...r, idmodelo: Number(idModelo), ...this.flagsOco(jsonData) }));
        this.loadingModelOps = false;
        if (this.modelRows.length === 0 && !recibioError) {
          this.errorMsg = 'No se encontraron operaciones para el modelo en la fecha de suscripción.';
        }
        this.calcularComparativa();
      }
    });
  }

  // Modo "Todos": mismo flujo que cargarOperacionesModelo + dispararModeloSse, pero como
  // Observable de un solo valor para poder combinarlo con forkJoin por cada subcartera.
  private obtenerOperacionesModeloParaSubcart(opt: SubcartOption): Observable<{ rows: operationinterface[]; idmercado: number }> {
    return new Observable(observer => {
      this.sseService.apiGetNew('parametrosmodelo?idmodelo=' + opt.idModelo).subscribe({
        next: (data: any) => {
          let condicionln: any;
          try {
            condicionln = JSON.parse(data.condicionln);
          } catch {
            observer.error(`No se pudieron leer las condiciones del modelo "${opt.nombre}".`);
            return;
          }
          let ln: any;
          try {
            ln = JSON.parse(data.lnList);
          } catch {
            ln = [];
          }

          const jsonData = this.construirJsonDataModelo(data, condicionln, ln);
          const ope: any[] = [];
          const sub = this.sseService.apiGetSse('ia/crearmodelojava?data=', true, encodeURIComponent(JSON.stringify(jsonData))).subscribe({
            next: (resp: any) => {
              if (resp.name === 'ope') {
                ope.push(resp.data);
              } else if (resp.name === 'err') {
                this.toastr.error(resp.data, `Error al calcular operaciones de "${opt.nombre}"`);
              }
            },
            error: () => observer.error(`Error de conexión al calcular las operaciones de "${opt.nombre}".`),
            complete: () => {
              const rows = ope.flatMap((batch: any[]) => batch)
                .map((r: operationinterface) => ({ ...r, idmodelo: Number(opt.idModelo), ...this.flagsOco(jsonData) }));
              observer.next({ rows, idmercado: condicionln.idmercado });
              observer.complete();
            }
          });
          this.todosSseSubscriptions.push(sub);
        },
        error: () => observer.error(`Error al obtener los parámetros del modelo "${opt.nombre}".`)
      });
    });
  }

  /** Carga el iso de los idNectum que aún no lo tienen (listarconid); si falla, se sigue sin él. */
  private cargarIsos(idNectums: number[]): Observable<void> {
    const faltan = Array.from(new Set(idNectums)).filter(id => id && !this.isoPorIdNectum.has(id));
    if (faltan.length === 0) return of(undefined);
    return this.sseService.apiPostNew('listarconid', faltan, this.data.licencia).pipe(
      map((resp: { idNectum: number; iso?: string }[]) => {
        (resp || []).forEach(r => { if (r.iso) this.isoPorIdNectum.set(r.idNectum, r.iso); });
      }),
      catchError(() => of(undefined))
    );
  }

  // Renumera isooper (ticker + contador, p. ej. "AAPL01") para que el contador de cada
  // instrumento (idNectum) continúe de una subcartera a la siguiente en vez de reiniciarse en
  // cada una. Recibe las filas agrupadas por subcartera, en el mismo orden que aparecen en el
  // desplegable, para que la primera subcartera quede sin cambios y las siguientes continúen
  // desde donde se quedó la anterior. Entrada y salida del mismo trade comparten el isooper
  // original (solo cambia tipoOperacion), así que se agrupan por idNectum+isooper original para
  // que ambas reciban el mismo isooper nuevo.
  private renumerarIsooperGlobal(filasPorSubcartera: operationinterface[][]): operationinterface[] {
    const contadorPorInstrumento = new Map<number, number>();
    const resultado: operationinterface[] = [];

    for (const filas of filasPorSubcartera) {
      const nuevoIsooperPorGrupo = new Map<string, string>();

      for (const fila of filas) {
        const grupoKey = `${fila.idNectum}|${fila.isooper}`;
        let nuevoIsooper = nuevoIsooperPorGrupo.get(grupoKey);

        if (nuevoIsooper === undefined) {
          // El prefijo es el iso (puede acabar en cifras, US30); sin iso conocido, el isooper
          // menos sus 2 últimas cifras.
          const iso = this.isoPorIdNectum.get(fila.idNectum);
          const prefijo = contadorIsooper(fila.isooper, iso) !== null ? iso!
            : (/\d{2}$/.test(fila.isooper) ? fila.isooper.slice(0, -2) : null);
          if (prefijo === null) {
            // No tiene el formato ticker+contador esperado: se deja tal cual, sin renumerar.
            nuevoIsooper = fila.isooper;
          } else {
            const siguiente = (contadorPorInstrumento.get(fila.idNectum) ?? 0) + 1;
            contadorPorInstrumento.set(fila.idNectum, siguiente);
            nuevoIsooper = formarIsooper(prefijo, siguiente);
          }
          nuevoIsooperPorGrupo.set(grupoKey, nuevoIsooper);
        }

        resultado.push(nuevoIsooper === fila.isooper ? fila : { ...fila, isooper: nuevoIsooper });
      }
    }

    return resultado;
  }

  private cargarTodasLasSubcarteras(): void {
    this.loadingModelOps = true;
    this.loadingParams = true;
    this.loadingContractSpecs = true;
    this.errorMsg = '';
    this.modelRows = [];
    this.riesgoParamsPorModelo = {};
    this.contractSpecs = {};

    const reales = this.subcartMeta;
    if (reales.length === 0) {
      this.loadingModelOps = false;
      this.loadingParams = false;
      this.loadingContractSpecs = false;
      return;
    }

    // Se procesan las subcarteras UNA A LA VEZ (concatMap), no todas en paralelo: ia/crearmodelojava
    // usa internamente conexiones JDBC compartidas a nivel de proceso (Conectar.java, campo estático
    // oConn, más campos singleton en CalculationServiceImpl) que no son seguras ante llamadas
    // concurrentes — lanzar todas las subcarteras a la vez producía errores intermitentes del backend
    // ("This statement/result set has been closed") al recalcular más de un modelo en simultáneo.
    from(reales).pipe(
      concatMap(opt => forkJoin({
        modelo: this.obtenerOperacionesModeloParaSubcart(opt),
        riesgo: this.obtenerRiesgoParaSubcart(opt.idCarteraOptima)
      })),
      toArray(),
      // El iso de cada instrumento hace falta para renumerar sin confundir sus cifras con el contador
      concatMap(resultados => this.cargarIsos(resultados.flatMap(r => r.modelo.rows.map(f => f.idNectum)))
        .pipe(map(() => resultados)))
    ).subscribe({
      next: (resultados) => {
        // Cada subcartera recalcula su modelo desde cero (isooper = ticker + contador reiniciado
        // en 0), así que combinarlas tal cual produce isooper repetidos entre subcarteras (p. ej.
        // "AAPL01" en dos subcarteras distintas). Además de confuso, esto es riesgoso: el backend
        // empareja entrada/salida por isooper+tipoOperacion+idcartera (sin filtrar por idmodelo,
        // ver CalculationServiceImpl.java:1698), así que un isooper duplicado entre subcarteras de
        // la misma suscripción podría emparejar mal operaciones de subcarteras distintas al
        // sincronizar. Se renumera para que el contador de cada instrumento continúe de una
        // subcartera a la siguiente, en el mismo orden del desplegable.
        this.modelRows = this.renumerarIsooperGlobal(reales.map((_, i) => resultados[i].modelo.rows));
        resultados.forEach((r, i) => {
          this.riesgoParamsPorModelo[reales[i].idModelo] = r.riesgo;
        });
        this.loadingModelOps = false;
        this.loadingParams = false;

        const mercadosUnicos = Array.from(new Set(
          resultados.map(r => r.modelo.idmercado).filter((m): m is number => !!m)
        ));
        this.cargarContractSpecsMultiples(mercadosUnicos);
      },
      error: (err) => {
        this.loadingModelOps = false;
        this.loadingParams = false;
        this.loadingContractSpecs = false;
        this.errorMsg = typeof err === 'string' ? err : 'Error al calcular la comparativa combinada de subcarteras.';
      }
    });
  }

  private cargarContractSpecsMultiples(mercados: number[]): void {
    if (mercados.length === 0) {
      this.loadingContractSpecs = false;
      this.calcularComparativa();
      return;
    }
    this.loadingContractSpecs = true;
    const llamadas = mercados.map(idmercado =>
      this.sseService.apiPostNew('metaapi/listadecontractsize', { idmercado }, this.data.licencia)
    );
    forkJoin(llamadas).subscribe({
      next: (resultados: any[][]) => {
        resultados.forEach((data) => {
          (data || []).forEach((spec: any) => {
            const idnectum = Number(spec.idnectum);
            this.contractSpecs[idnectum] = {
              contractSize: Number(spec.contract_size) > 0 ? Number(spec.contract_size) : 1,
              minVolume: Number(spec.min_volume) || 0,
              volumeStep: Number(spec.volume_step) || 0
            };
          });
        });
        this.loadingContractSpecs = false;
        this.calcularComparativa();
      },
      error: () => {
        this.loadingContractSpecs = false;
        this.toastr.warning('No se pudieron obtener los tamaños de contrato del mercado; revisa la cantidad calculada antes de sincronizar', 'Sincronizar');
        this.calcularComparativa();
      }
    });
  }

  // "Cartera actual": compara lo mismo que produce Diversificador -> "Análisis De Cartera" para
  // esta suscripción (GET /Recalcular con fecharegistrosuscripcion, evento SSE 'pos' = posiciones
  // ENTRADA actualmente abiertas — NO el historial completo de revisarModeloBrokerIajavaDesde, que
  // incluye SALIDA y no es comparable contra "posiciones abiertas") contra la cartera real actual
  // del broker (BrokerService.valBrokValPos$, refrescado vía updateactualizarCarteraBroker(true) —
  // mismo mecanismo que ya usan opeentrada-dialog/opesalida-dialog tras registrar una orden, en vez
  // de duplicar el Recalcular de /broker). Los parámetros de riesgo/capital para el cálculo
  // salen de la cartera ÓPTIMA de la suscripción (parametros/idCarteraOptima, una por subcartera,
  // fusionados con CarteraOptimaParamsService — el mismo servicio que usa Diversificador para armar
  // divOptimalWallet), no de la cartera real del broker, para garantizar el mismo resultado que
  // vería el usuario en Diversificador.
  private cargarComparativaCarteraActual(): void {
    if (!this.fecharegistrosuscripcion) {
      this.errorMsg = 'Esta cartera no tiene una suscripción activa para comparar.';
      return;
    }
    this.errorMsg = '';
    this.modelRows = [];
    this.brokerRows = [];

    // idcarterasuscrita es el id PADRE (tbl_cartera_modelo.idcartera2) -- tbl_operacion nunca tiene
    // filas con ese id (las posiciones reales quedan bajo cada subcartera). idsubcarterascartera es
    // el mismo CSV de ids de subcartera que ya usa el enlace "Cart." para /portfolio-labs
    // (suscripciones-confirmar.component.html), que si funciona.
    const idCarterasCsv: string = this.listaSuscripciones[0]?.idsubcarterascartera || String(this.data.idcarterasuscrita);
    const idsSubcarterasReales = idCarterasCsv.split(',').map(s => s.trim());
    this.cargarFlagsOcoModelos(idCarterasCsv);
    this.cargarHistorialCartera();

    // Mapa idSubcartera real -> SubcartOption, para resolver el idCarteraOptima de cada subcartera
    // que efectivamente se envía en idCarteras. Si alguna id de idCarteras no tiene entrada aquí
    // (subcartMeta recortado por un string_agg desalineado en el backend), se usan los defaults
    // abajo en vez de desalinear tiposriesgo/valoresriesgo respecto a idCarteras.
    const optionPorIdSubcartera = new Map(
      this.subcartMeta
        .filter(o => o.idSubcartera)
        .map(o => [o.idSubcartera, o] as const)
    );

    // parametros/{id} se pide una sola vez por idCarteraOptima único, aunque varias subcarteras
    // compartan el mismo idCarteraOptima (no hay unicidad garantizada en tbl_cartera).
    const idsCarteraOptimaUnicos = Array.from(new Set(
      idsSubcarterasReales
        .map(id => optionPorIdSubcartera.get(id)?.idCarteraOptima)
        .filter((id): id is string => !!id)
    ));

    this.loadingModelOps = true;
    const parametros$ = idsCarteraOptimaUnicos.length > 0
      ? forkJoin(idsCarteraOptimaUnicos.map(id =>
          this.sseService.apiGetNew('parametros/' + id).pipe(catchError(() => of(null)))
        ))
      : of([]);

    parametros$.subscribe({
      next: (respuestas: any[]) => {
        const respuestaPorIdCarteraOptima = new Map(
          idsCarteraOptimaUnicos.map((id, i) => [id, respuestas[i]])
        );

        const wallet: OptimalWallet = { ...this.data.optimalWallet };
        const riesgoAcumulado = { tiporiesgo: '', valoriresgo: 0 };
        const tiposriesgo: string[] = [];
        const valoresriesgo: number[] = [];

        // Se expande de vuelta 1:1 con idsSubcarterasReales (=idCarteras), no con el array
        // deduplicado de arriba, para que el backend (RecalcularOperacion) indexe correctamente.
        idsSubcarterasReales.forEach((idSub) => {
          const opt = optionPorIdSubcartera.get(idSub);
          const carteraData = opt?.idCarteraOptima
            ? respuestaPorIdCarteraOptima.get(opt.idCarteraOptima)
            : undefined;

          if (carteraData) {
            this.carteraOptimaParamsService.fusionarUnaCarteraOptima(wallet, carteraData, riesgoAcumulado);
          }
          tiposriesgo.push(carteraData?.tiporiesgo !== undefined ? carteraData.tiporiesgo : this.DEFAULT_TIPORIESGO);
          valoresriesgo.push(carteraData?.riesgo !== undefined ? Number(carteraData.riesgo) : this.DEFAULT_RIESGO);
        });

        this.dispararRecalcularPosicionesModelo({
          ...wallet,
          // idcartera/idModelo en 0: wallet ya trae el idcartera REAL del broker (data.optimalWallet),
          // y Recalcular prioriza "idcartera>0" (lee tbl_operacion de esa cartera directamente) sobre
          // la rama de fecharegistrosuscripcion -- sin esto, idCarteras/fecharegistrosuscripcion de
          // abajo se ignoraban por completo y se leia el historico real en vez del preview.
          idcartera: 0,
          idModelo: 0,
          // El preview del modelo es teórico: Recalcular solo aplica comisionbroker si simulado=true
          // (RecalcularOperacion), y la cartera real del broker llega con simulado=false.
          simulado: true,
          idCarteras: idCarterasCsv,
          metaapi: this.data.optimalWallet.tipoBroker === 'METAAPI',
          tiposriesgo: tiposriesgo.join(','),
          valoresriesgo: valoresriesgo.join(','),
          fecharegistrosuscripcion: this.fecharegistrosuscripcion
        });
      },
      error: () => {
        this.loadingModelOps = false;
        this.errorMsg = 'Error al cargar los parámetros de la cartera óptima de la suscripción.';
      }
    });

    if (this.brokerPosSubscription) {
      this.brokerPosSubscription.unsubscribe();
    }
    this.loadingBrokerOps = true;
    // Refresca la cartera real vía el mismo mecanismo que ya usan opeentrada-dialog/opesalida-dialog
    // tras registrar una orden (evita duplicar el Recalcular concurrente: broker.component.ts:613-622
    // escucha esta señal y vuelve a llamar su propio getOptimalWallet, que puebla valBrokValPos$).
    this.brokerService.updateactualizarCarteraBroker(true);
    this.brokerPosSubscription = this.brokerService.valBrokValPos$.subscribe({
      next: (datos: any) => {
        this.brokerRows = (datos || []).flat();
        this.loadingBrokerOps = false;
        this.calcularComparativa();
      },
      error: () => {
        this.loadingBrokerOps = false;
        this.toastr.error('Error al obtener la cartera real del broker', 'Sincronizar');
      }
    });
  }

  // Mismo endpoint y convención que Salir fin de día (construirFilas): en bajista el tipo crudo se
  // invierte para que "ENTRADA" sea siempre la apertura. Incluye las salidas ya ejecutadas de
  // tbl_operacion, sea cual sea su origen (fin de día, tabla Posiciones, SL/TP, modelo).
  private cargarHistorialCartera(): void {
    const idcartera = this.data.optimalWallet.idcartera;
    const licencia = this.data.licencia || this.cookieService.get('auth_token');
    this.isoopersCerrados = new Set();
    this.cierresSlTpBroker = new Map();
    this.loadingHistorial = true;
    (this.sseService.apiGetWithAuth('operaciones/' + idcartera, licencia) as Observable<any[]>).pipe(
      catchError(() => of([] as any[]))
    ).subscribe((ops: any[]) => {
      const propias = (ops || []).filter(o => o.idcartera === idcartera && o.isooper);
      const tipo = (o: any) => o.bajista ? (o.tipoOperacion === 'ENTRADA' ? 'SALIDA' : 'ENTRADA') : o.tipoOperacion;
      const iso = (o: any) => String(o.isooper).toUpperCase();
      const conEntrada = new Set(propias.filter(o => tipo(o) === 'ENTRADA').map(iso));
      propias.filter(o => tipo(o) === 'SALIDA' && conEntrada.has(iso(o)))
        .forEach(o => {
          this.isoopersCerrados.add(iso(o));
          // ordertype = la orden con que se cerró: STP/LMT es la OCO del broker; MKT un cierre a propósito
          // (fin de día, X, manual), cuya reentrada sí se propone
          const ordertype = String(o.ordertype ?? '').toUpperCase();
          if (ordertype !== 'STP' && ordertype !== 'LMT') return;
          const ts = this.aMs(o.lastexecutiontimer) || this.aMs(o.fechaOperacion);
          const clave = iso(o) + '|' + (o.bajista ? 'B' : 'A');
          const previo = this.cierresSlTpBroker.get(clave);
          if (!previo || ts > previo.ts) this.cierresSlTpBroker.set(clave, { tipo: ordertype === 'STP' ? 'SL' : 'TP', ts });
        });
      this.loadingHistorial = false;
      this.calcularComparativa();
    });
  }

  // Asigna a cada reentrada el siguiente isooper real. Varias reentradas del mismo instrumento
  // reciben números consecutivos: broker/contador no ve lo que aún no se ha enviado.
  private asignarIsooperReentradas(filas: ComparisonRow[]): void {
    const siguientePorIdNectum = new Map<number, string>();
    const faltantes = new Set<number>();
    filas.filter(r => r.reentrada).forEach(r => {
      const isooper = siguientePorIdNectum.get(r.idNectum) ?? this.contadorPorIdNectum.get(r.idNectum);
      if (!isooper) {
        faltantes.add(r.idNectum);
        return;
      }
      r.reentradaIsooper = isooper;
      siguientePorIdNectum.set(r.idNectum, incrementarIsooper(isooper, this.isoPorIdNectum.get(r.idNectum)));
    });
    this.pedirContadores(Array.from(faltantes));
  }

  private pedirContadores(idNectums: number[]): void {
    const nuevos = idNectums.filter(id => !this.contadoresPedidos.has(id));
    if (nuevos.length === 0) return;
    nuevos.forEach(id => this.contadoresPedidos.add(id));
    const idcartera = this.data.optimalWallet.idcartera;
    forkJoin(nuevos.map(idnectum =>
      this.sseService.apiPostNew('broker/contador', { idcartera, idnectum }, this.data.licencia).pipe(
        map((d: any) => {
          if (d?.iso) this.isoPorIdNectum.set(idnectum, d.iso);
          return d?.contador ? String(d.contador) : null;
        }),
        catchError(() => of(null))
      )
    )).subscribe((contadores: (string | null)[]) => {
      contadores.forEach((c, i) => { if (c) this.contadorPorIdNectum.set(nuevos[i], c); });
      if (contadores.some(c => !c)) {
        this.toastr.warning('No se pudo obtener el ID nuevo de alguna reentrada; quedará sin seleccionar', 'Sincronizar');
      }
      this.calcularComparativa();
    });
  }

  private cargarFlagsOcoModelos(idCarterasCsv: string): void {
    this.flagsOcoPorModelo.clear();
    this.sseService.apiGetNew('parametrosmodelos?idcarteras=' + idCarterasCsv).subscribe({
      next: (modelos: any[]) => {
        (modelos || []).forEach(m => this.flagsOcoPorModelo.set(Number(m.id), {
          stoplossactivo: m.stopLossActivo === true,
          profitactivo: m.profitActivo === true
        }));
      },
      error: () => this.toastr.warning('No se pudo leer si los modelos tienen Stop Loss/Profit activos; las órdenes OCO saldrán sin SL/TP', 'Sincronizar')
    });
  }

  // Como en la automatización: carteraid = cartera suscrita (padre) y subcarteraid = subcartera del modelo.
  // Con subcarteraid el backend enlaza la cartera de la orden, de la que salen los SL/TP activos de la
  // operación (las filas de Recalcular llegan con ambos a 0).
  private conCarteras(row: operationinterface): operationinterface {
    const sub = this.subcartMeta.find(o => Number(o.idModelo) === Number(row.idmodelo))
      ?? (this.subcartMeta.length === 1 ? this.subcartMeta[0] : undefined);
    return {
      ...row,
      carteraid: row.carteraid || this.data.idcarterasuscrita,
      subcarteraid: row.subcarteraid || Number(sub?.idSubcartera) || 0
    };
  }

  // Si la fila no trae idmodelo y la cartera solo tiene un modelo, se usan los flags de ese modelo.
  private conFlagsOco(row: operationinterface): operationinterface {
    const flags = this.flagsOcoPorModelo.get(Number(row.idmodelo))
      ?? (this.flagsOcoPorModelo.size === 1 ? [...this.flagsOcoPorModelo.values()][0] : undefined);
    return flags ? { ...row, ...flags } : row;
  }

  // Dispara el mismo GET /Recalcular que usa Diversificador -> "Análisis De Cartera" y se queda
  // solo con el evento 'pos' (posiciones abiertas), igual que diversificador.component.ts hace con
  // el evento 'ope' para armar modelRows en el resto de modos de este diálogo.
  private dispararRecalcularPosicionesModelo(payload: any): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
    }
    let pos: any[] = [];
    this.sseSubscription = this.sseService.apiGetSse('Recalcular?', true, payload).subscribe({
      next: (resp: any) => {
        if (resp.name === 'pos') {
          pos.push(resp.data);
        } else if (resp.name === 'err') {
          this.toastr.error(resp.data, 'Error al calcular la cartera del diversificador');
        }
      },
      error: () => {
        this.loadingModelOps = false;
        this.errorMsg = 'Error de conexión al calcular la cartera del diversificador.';
      },
      complete: () => {
        this.modelRows = pos.flat();
        this.loadingModelOps = false;
        if (this.modelRows.length === 0 && !this.errorMsg) {
          this.errorMsg = 'No se encontraron posiciones abiertas para el diversificador en esta fecha.';
        }
        this.calcularComparativa();
      }
    });
  }

  private cargarOperacionesBroker(): void {
    this.loadingBrokerOps = true;
    this.brokerRows = [];
    this.sseService.apiPostNew('listaroperaciones', {
      idcartera: this.data.optimalWallet.idcartera,
      idModelo: 0,
      idcarteras: '0,0,0,0,0'
    }, this.data.licencia).subscribe({
      next: (data: operationinterface[]) => {
        this.brokerRows = data || [];
        this.loadingBrokerOps = false;
        this.calcularComparativa();
      },
      error: () => {
        this.loadingBrokerOps = false;
        this.toastr.error('Error al obtener las operaciones del broker', 'Sincronizar');
      }
    });
  }

  private calcularComparativa(): void {
    if (this.cargandoComparativa) return;
    // La clave incluye idmodelo (no solo compañía+tipo) para que una operación de una
    // subcartera nunca se confunda con la de otra que opere la misma compañía — esto ya
    // cubre, sin filtros aparte, tanto el modo de una subcartera como el modo "Todos".
    const key = (r: operationinterface) => `${r.idmodelo}|${r.nombreCompania}|${r.tipoOperacion}`;
    // tipoOperacion crudo se invierte en bajista (abrir un corto se codifica como "SALIDA"),
    // misma convención que tipoOperacionDisplay() y el backend (iaController.java,
    // ModeloServiceImpl.java) — así se identifica una apertura nueva real, que es la única
    // que necesita re-sizing con calcularCantidadAcciones().
    const esAperturaNueva = (r: operationinterface) => r.tipoOperacion === (r.bajista ? 'SALIDA' : 'ENTRADA');

    const esCarteraActual = this.selectedIdModelo === this.CARTERA_ACTUAL_ID;
    // "Cartera actual": se empareja por posición (instrumento + sentido), sin idmodelo: el preview
    // de Recalcular usa el idmodelo ACTUAL de cada subcartera, mientras que la posición real conserva
    // el idmodelo con el que se guardó en tbl_operacion (cambia si el modelo se regenera, p. ej. por
    // fecha inicial), y esa diferencia hacía salir la misma posición como ENTRADA y como SALIDA.
    // El broker tiene una posición neta por instrumento sin noción de subcartera, así que tampoco hace
    // falta idmodelo para distinguir subcarteras: basta contar cuántas pide el modelo vs cuántas hay.
    let modelMissing: Set<operationinterface>;
    let brokerSobrantes: operationinterface[] = [];
    if (esCarteraActual) {
      ({ modelMissing, brokerSobrantes } = this.emparejarPosiciones(this.modelRows, this.brokerRows));
    } else {
      const brokerKeys = new Set(this.brokerRows.map(key));
      modelMissing = new Set(this.modelRows.filter(r => !brokerKeys.has(key(r))));
    }

    const filasEntrada: ComparisonRow[] = this.modelRows.map((r) => {
      const missing = modelMissing.has(r);
      // Solo hace falta un tamaño de posición real para las filas que realmente
      // se podrían enviar al broker y que son una apertura nueva; el resto conserva su
      // valor tal cual llegó. "Cartera actual" viene de Recalcular, que ya calculó
      // cantidadacciones con el sizing real (incluye el gating de margen/riesgo que el
      // cálculo aislado de abajo no replica). Una fila SALIDA ya trae de
      // ia/crearmodelojava la cantidad heredada de su entrada emparejada
      // (CalculationServiceImpl.java: "si operacion es salida agregar cantidadacciones de
      // la entrada") — recalcularla con la fórmula de entrada nueva sería incorrecto.
      const cantidadacciones = (missing && this.selectedIdModelo !== this.CARTERA_ACTUAL_ID && esAperturaNueva(r))
        ? this.calcularCantidadAcciones(r)
        : r.cantidadacciones;
      // Reentrada: el isooper que propone el modelo ya se abrió y cerró en esta cartera.
      const reentrada = missing && esCarteraActual && esAperturaNueva(r)
        && this.isoopersCerrados.has((r.isooper ?? '').toUpperCase());
      // El broker cerró por SL/TP a mitad de una barra que el modelo aún no ha consolidado (su última
      // barra es anterior al cierre): sin hora de barra se bloquea por prudencia.
      const cierreBroker = reentrada
        ? this.cierresSlTpBroker.get((r.isooper ?? '').toUpperCase() + '|' + (r.bajista ? 'B' : 'A'))
        : undefined;
      const ultimaBarra = this.aMs((r as any).horaCotizacion);
      const pendienteBarra = !!cierreBroker && (!ultimaBarra || cierreBroker.ts >= ultimaBarra);
      return { ...r, cantidadacciones, missing, reentrada, cierreBroker, pendienteBarra };
    });
    this.asignarIsooperReentradas(filasEntrada);

    // "Cartera actual": diff inverso — una posición que el broker tiene abierta pero que el
    // modelo ya no contempla en sus posiciones actuales (modelRows = evento 'pos' de
    // Recalcular) debe proponerse como cierre. brokerRows aquí viene de tbl_operacion filtrado
    // por idcartera (apiController.java:2534), así que nunca incluye posiciones ajenas al
    // modelo. Se invierte solo el tipoOperacion crudo (nunca bajista) para que el resto del
    // componente (tipoOperacionDisplay, construirOrdersData) lo trate como el cierre que es;
    // la cantidad se deja tal cual (posición realmente abierta), sin recalcular.
    const filasSalida: ComparisonRow[] = brokerSobrantes.map((b) => ({
      ...b,
      tipoOperacion: b.tipoOperacion === 'SALIDA' ? 'ENTRADA' : 'SALIDA',
      missing: true
    }));

    // Solo se muestran las filas pendientes de sincronizar: las ya emparejadas con el broker
    // (missing=false, "Sincronizado") no requieren acción y se omiten de la tabla.
    this.comparisonRows = [...filasEntrada.filter(r => r.missing), ...filasSalida];
    this.reconciliarSeleccion();
    this.resolverContractSpecsFaltantes();
    this.resolverConidFaltantes();
  }

  // Empareja posiciones abiertas modelo vs broker por instrumento + sentido (idNectum|bajista),
  // contando ocurrencias (2 subcarteras con AAPL vs 1 en broker -> falta 1). Primero se emparejan
  // las que coinciden también en isooper (AAPL01<->AAPL01) para que la fila que quede como faltante
  // sea la correcta; después, las restantes del mismo instrumento/sentido entre sí.
  private emparejarPosiciones(modelRows: operationinterface[], brokerRows: operationinterface[]):
    { modelMissing: Set<operationinterface>; brokerSobrantes: operationinterface[] } {
    const keyPosicion = (r: operationinterface) => `${r.idNectum}|${!!r.bajista}`;
    const keyIsooper = (r: operationinterface) => `${keyPosicion(r)}|${(r.isooper ?? '').toUpperCase()}`;

    const modelPendientes = new Set(modelRows);
    const brokerPendientes = new Set(brokerRows);

    const emparejar = (keyFn: (r: operationinterface) => string) => {
      const brokerPorKey = new Map<string, operationinterface[]>();
      brokerPendientes.forEach((b) => {
        const k = keyFn(b);
        if (!brokerPorKey.has(k)) brokerPorKey.set(k, []);
        brokerPorKey.get(k)!.push(b);
      });
      modelPendientes.forEach((m) => {
        const candidato = brokerPorKey.get(keyFn(m))?.shift();
        if (!candidato) return;
        modelPendientes.delete(m);
        brokerPendientes.delete(candidato);
      });
    };
    emparejar(keyIsooper);
    emparejar(keyPosicion);

    console.log('[Sincronizar] keys modelo/broker', {
      modelo: modelRows.map(r => `${keyIsooper(r)} idmodelo=${r.idmodelo}`),
      broker: brokerRows.map(r => `${keyIsooper(r)} idmodelo=${r.idmodelo}`),
      faltantes: Array.from(modelPendientes).map(r => r.isooper),
      sobrantes: Array.from(brokerPendientes).map(r => r.isooper)
    });

    return { modelMissing: modelPendientes, brokerSobrantes: Array.from(brokerPendientes) };
  }

  // Respaldo en vivo: listadecontractsize lee tbl_metaapi_symbol_specifications (tabla cacheada), que
  // puede no tener aún el símbolo aunque MetaAPI sí lo conozca. Mismo patrón que
  // opeentrada-dialog.component.ts:cargarSymbolSpec() — un lookup en vivo por símbolo, solo para las
  // filas "faltantes" que quedaron sin spec tras la respuesta de BD. Se intenta una única vez por
  // subcart seleccionado (contractSpecsFallbackAttempted) para no reintentar en bucle si el símbolo
  // realmente no existe en MetaAPI.
  private resolverContractSpecsFaltantes(): void {
    if (this.contractSpecsFallbackAttempted) return;
    if (this.data.optimalWallet.tipoBroker !== 'METAAPI') return;

    const pendientes = new Map<string, number>(); // símbolo (sin " CFD") -> idNectum
    this.comparisonRows.forEach((row) => {
      if (row.missing && !this.contractSpecs[row.idNectum]) {
        pendientes.set(row.nombreCompania.split(' CFD')[0], row.idNectum);
      }
    });
    if (pendientes.size === 0) return;

    console.log('[Sincronizar][fallback MetaAPI] símbolos sin spec en BD:', Array.from(pendientes.keys()));
    this.contractSpecsFallbackAttempted = true;
    const accountId = this.brokerAdapterFactory.getAccountId(this.data.optimalWallet);
    const entries = Array.from(pendientes.entries());
    const llamadas = entries.map(([symbol]) => this.metaApiService.getSymbolSpecification(accountId, symbol));

    forkJoin(llamadas).subscribe((resultados: any[]) => {
      resultados.forEach((spec, i) => {
        const [symbol, idNectum] = entries[i];
        console.log(`[Sincronizar][fallback MetaAPI] ${symbol} (idNectum=${idNectum}) ->`, spec);
        if (!spec) return;
        this.contractSpecs[idNectum] = {
          contractSize: Number(spec.contractSize) > 0 ? Number(spec.contractSize) : 1,
          minVolume: Number(spec.minVolume) || 0,
          volumeStep: Number(spec.volumeStep) || 0
        };
      });
      console.log('[Sincronizar] contractSpecs (tras fallback en vivo):', this.contractSpecs);
      this.calcularComparativa();
    });
  }

  // Resolución de conid para IBKR: primero se consulta tbl_compania (endpoint listarconid) por
  // los idNectum "faltantes"; solo si esa consulta no trae el conid (compañía nunca backfilleada
  // por admin.component.ts:procesarCompania) se cae al respaldo en vivo contra el Gateway local de
  // IBKR (/iserver/secdef/search). Mismo patrón que resolverContractSpecsFaltantes: un único intento
  // por subcart seleccionado (conidFallbackAttempted).
  private resolverConidFaltantes(): void {
    if (this.conidFallbackAttempted) return;
    if (this.data.optimalWallet.tipoBroker === 'METAAPI') return;

    const pendientes = new Map<string, number>(); // símbolo (sin " CFD") -> idNectum
    this.comparisonRows.forEach((row) => {
      if (row.missing && !(Number(row.conid) > 0)) {
        pendientes.set(row.nombreCompania.split(' CFD')[0].toUpperCase(), row.idNectum);
      }
    });
    if (pendientes.size === 0) return;

    console.log('[Sincronizar] símbolos sin conid:', Array.from(pendientes.keys()));
    this.conidFallbackAttempted = true;
    const entries = Array.from(pendientes.entries());
    const idNectums = entries.map(([, idNectum]) => idNectum);

    this.sseService.apiPostNew('listarconid', idNectums, this.data.licencia).subscribe({
      next: (resp: { idNectum: number; conid: number }[]) => {
        const conidPorIdNectum = new Map<number, number>(
          (resp || [])
            .filter((r) => Number(r.conid) > 0)
            .map((r) => [r.idNectum, Number(r.conid)])
        );

        this.modelRows = this.modelRows.map((r) => {
          const conid = conidPorIdNectum.get(r.idNectum);
          return conid ? { ...r, conid } : r;
        });
        // Las filas sintéticas de salida de "Cartera actual" viven en brokerRows, no en
        // modelRows — sin este mismo patch aquí quedarían atascadas como no seleccionables
        // aunque el conid se resuelva.
        this.brokerRows = this.brokerRows.map((r) => {
          const conid = conidPorIdNectum.get(r.idNectum);
          return conid ? { ...r, conid } : r;
        });

        const pendientesTrasDb = entries.filter(([, idNectum]) => !conidPorIdNectum.has(idNectum));
        if (pendientesTrasDb.length === 0) {
          this.calcularComparativa();
          return;
        }

        this.resolverConidPorIbkr(pendientesTrasDb);
      },
      error: () => {
        console.warn('[Sincronizar] no se pudo consultar listarconid, se intenta directo con IBKR');
        this.resolverConidPorIbkr(entries);
      }
    });
  }

  // Respaldo en vivo para IBKR: solo se llega aquí para símbolos cuyo conid no está en tbl_compania
  // (nunca backfilleado por admin.component.ts:procesarCompania) o si listarconid falló.
  private resolverConidPorIbkr(entries: [string, number][]): void {
    const llamadas = entries.map(([symbol]) =>
      this.oauthService.apiGetIbrk('/iserver/secdef/search?symbol=' + symbol).pipe(
        map((resp: any) => Array.isArray(resp)
          ? Number(resp.find((i: any) => (i.symbol ?? '').toUpperCase() === symbol && i.conid)?.conid) || null
          : null),
        catchError(() => of(null)) // Gateway de IBKR caído o símbolo no encontrado -> no aborta el forkJoin
      )
    );

    forkJoin(llamadas).subscribe((resultados: (number | null)[]) => {
      const resueltos = entries
        .map(([symbol, idNectum], i) => ({ symbol, idNectum, conid: resultados[i] }))
        .filter((r): r is { symbol: string; idNectum: number; conid: number } => !!r.conid);

      console.log('[Sincronizar][fallback IBKR] resultados:', resueltos);

      if (resueltos.length === 0) {
        this.toastr.warning(
          `No se pudo resolver el conid de IBKR para ${entries.length} símbolo(s); verifique que el Gateway de IBKR esté activo`,
          'Sincronizar'
        );
        return;
      }
      if (resueltos.length < entries.length) {
        this.toastr.warning(
          `${entries.length - resueltos.length} símbolo(s) no se pudieron resolver en IBKR y quedarán no seleccionables`,
          'Sincronizar'
        );
      }

      this.modelRows = this.modelRows.map((r) => {
        const encontrado = resueltos.find((x) => x.idNectum === r.idNectum);
        return encontrado ? { ...r, conid: encontrado.conid } : r;
      });
      // Ídem: propagar también a brokerRows para las filas sintéticas de salida de
      // "Cartera actual" (ver comentario en resolverConidFaltantes).
      this.brokerRows = this.brokerRows.map((r) => {
        const encontrado = resueltos.find((x) => x.idNectum === r.idNectum);
        return encontrado ? { ...r, conid: encontrado.conid } : r;
      });

      resueltos.forEach(({ symbol, conid }) => {
        this.sseService.apiPost('actualizarConid', true, { conid, iso: symbol }, 2)
          .catch(() => console.warn('[Sincronizar][fallback IBKR] no se pudo persistir conid para', symbol));
      });

      this.calcularComparativa();
    });
  }

  nombreSubcartera(idmodelo: number): string {
    const opt = this.subcartMeta.find(o => Number(o.idModelo) === Number(idmodelo));
    return opt ? opt.nombre : String(idmodelo);
  }

  // Solo presentación: gestion/cartera/operations.component ya usan esta misma convención
  // (tipoOperacion crudo tiene semántica de compra/venta; en bajista, abrir el trade es una
  // venta -> se muestra como "ENTRADA" y cerrarlo es una compra -> se muestra como "SALIDA").
  // No se muta row.tipoOperacion: ese valor crudo sigue usándose tal cual para la clave de
  // comparación (calcularComparativa), el ordenamiento y el payload que se envía al backend.
  tipoOperacionDisplay(row: ComparisonRow): string {
    if (row.bajista) {
      if (row.tipoOperacion === 'SALIDA') return 'ENTRADA ↓';
      if (row.tipoOperacion === 'ENTRADA') return 'SALIDA ↓';
    } else if (row.tipoOperacion === 'SALIDA' || row.tipoOperacion === 'ENTRADA') {
      return `${row.tipoOperacion} ↑`;
    }
    return row.tipoOperacion;
  }

  // Solo se puede seleccionar (y por tanto enviar) una fila si además de estar
  // "faltante" se le pudo calcular un tamaño de posición válido (> 0). Para brokers
  // distintos de MetaAPI (IBKR) además se exige un conid resuelto (> 0): sin esto, una fila
  // cuyo conid no se pudo resolver (ni desde BD ni desde el fallback en vivo de
  // resolverConidFaltantes) se enviaría a IBKR con un contract id inválido (0).
  esSeleccionable(row: ComparisonRow): boolean {
    if (!row.missing || row.cantidadacciones <= 0) return false;
    if (row.pendienteBarra) return false;
    if (row.reentrada && !row.reentradaIsooper) return false;
    if (this.data.optimalWallet.tipoBroker !== 'METAAPI' && !(Number(row.conid) > 0)) return false;
    return true;
  }

  // calcularComparativa() se re-ejecuta en cada emisión de valBrokValPos$ y crea filas nuevas
  // ({ ...r }); SelectionModel compara por referencia, así que sin esto la selección conserva
  // objetos obsoletos: el checkbox aparece desmarcado, el contador crece al volver a marcarlo y
  // al sincronizar se enviaría la misma operación varias veces.
  private claveFila(r: ComparisonRow): string {
    return `${r.idmodelo}|${r.isooper}|${r.idNectum}|${r.tipoOperacion}|${r.bajista}`;
  }

  private reconciliarSeleccion(): void {
    if (this.selection.isEmpty()) return;
    const claves = new Set(this.selection.selected.map(r => this.claveFila(r)));
    this.selection.clear();
    this.comparisonRows
      .filter(r => claves.has(this.claveFila(r)) && this.esSeleccionable(r))
      .forEach(r => this.selection.select(r));
  }

  toggleRow(row: ComparisonRow): void {
    if (!this.esSeleccionable(row)) return;
    this.selection.toggle(row);
  }

  isAllSelectableSelected(): boolean {
    const seleccionables = this.comparisonRows.filter(r => this.esSeleccionable(r));
    return seleccionables.length > 0 && seleccionables.every(r => this.selection.isSelected(r));
  }

  sortData(sort: Sort): void {
    const data = [...this.comparisonRows];
    if (!sort.active || sort.direction === '') {
      this.comparisonRows = data;
      return;
    }
    const isAsc = sort.direction === 'asc';
    data.sort((a, b) => {
      switch (sort.active) {
        case 'subcartera':
          return compare(this.nombreSubcartera(a.idmodelo), this.nombreSubcartera(b.idmodelo), isAsc);
        case 'isooper':
          return compare(a.isooper, b.isooper, isAsc);
        case 'nombreCompania':
          return compare(a.nombreCompania, b.nombreCompania, isAsc);
        case 'tipoOperacion':
          return compare(a.tipoOperacion, b.tipoOperacion, isAsc);
        case 'fechaOperacion':
          return compare(new Date(a.fechaOperacion), new Date(b.fechaOperacion), isAsc);
        case 'precioentradareal':
          return compare(a.precioentradareal, b.precioentradareal, isAsc);
        case 'preciosalidareal':
          return compare(a.preciosalidareal, b.preciosalidareal, isAsc);
        case 'stoploss':
          return compare(Number(a['stoploss']), Number(b['stoploss']), isAsc);
        case 'profit':
          return compare(Number(a['profit']), Number(b['profit']), isAsc);        case 'cantidadacciones':
          return compare(a.cantidadacciones, b.cantidadacciones, isAsc);
        case 'beneficioperdida':
          return compare(a.beneficioperdida, b.beneficioperdida, isAsc);
        case 'origen':
          return compare(a.missing, b.missing, isAsc);
        default:
          return 0;
      }
    });
    this.comparisonRows = data;
  }

  toggleAll(): void {
    const seleccionables = this.comparisonRows.filter(r => this.esSeleccionable(r));
    if (this.isAllSelectableSelected()) {
      seleccionables.forEach(r => this.selection.deselect(r));
    } else {
      seleccionables.forEach(r => this.selection.select(r));
    }
  }

  sincronizarSeleccionadas(): void {
    if (this.automatizacionActiva) {
      this.avisarAutomatizacionActiva();
      return;
    }
    // Guarda de seguridad final: nunca enviar una orden sin un tamaño de posición
    // válido calculado, aunque la selección ya debería excluirlas (esSeleccionable).
    const seleccionadas = this.selection.selected.filter(r => r.cantidadacciones > 0);
    const omitidas = this.selection.selected.length - seleccionadas.length;
    if (seleccionadas.length === 0) {
      this.toastr.warning('Seleccione al menos una operación con un tamaño de posición válido', 'Sincronizar');
      return;
    }
    if (omitidas > 0) {
      this.toastr.warning(`${omitidas} operación(es) omitida(s) por no tener un tamaño de posición calculable`, 'Sincronizar');
    }
    // Recordatorio del toggle "Ordenes oco" de /broker: muestra su estado actual y, si se cambia
    // aquí, cambia al momento ese mismo estado (no es un OCO aparte para la sincronización).
    const reentradas = seleccionadas.filter(r => r.reentradaIsooper).length;
    const avisoReentradas = reentradas > 0 ? ` Incluye ${reentradas} reentrada(s) de posiciones ya cerradas (ID nuevo).` : '';
    const confirmData: ConfirmDialogData = {
      msj: `¿Sincronizar ${seleccionadas.length} posicion(es) seleccionada(s)?${avisoReentradas}`, icon: 'warning',
      toggle: {
        etiqueta: 'ORDENES OCO', valor: !!this.data.getOco?.(), tooltip: 'Enviar órdenes con Stop Loss y Take Profit (OCO)',
        cambio: (oco) => this.data.setOco?.(oco),
        detalle: (oco) => oco ? 'Sí se enviarán stoploss y profit' : 'No se enviarán stoploss y profit'
      }
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '380px', height: '310px', panelClass: this.data.isDark ? 'dark-theme' : 'light-theme',
      data: confirmData
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (!result) return;
      // Pudo activarse mientras el confirm estaba abierto.
      if (this.automatizacionActiva) {
        this.avisarAutomatizacionActiva();
        return;
      }
      this.sincronizarOperaciones.emit(seleccionadas.map(r =>
        this.conCarteras(this.conFlagsOco(r.reentradaIsooper ? { ...r, isooper: r.reentradaIsooper } : r))));
      this.dialogRef.close();
    });
  }

  private avisarAutomatizacionActiva(): void {
    this.toastr.warning('Detenga la automatización de esta cartera (pestaña Automatizado) antes de sincronizar operaciones', 'Sincronizar');
  }

  /** Igual que el asistente de condiciones: minimizado solo deja la cabecera para seguir operando en /broker. */
  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
    if (this.isMinimized) {
      this.dialogRef.updateSize('420px', 'auto');
    } else {
      this.dialogRef.updateSize('90%', '');
    }
  }

  cerrar(): void {
    this.dialogRef.close();
  }
}

// "AAPL02" -> "AAPL03", "US30102" -> "US30103": mismo formato que OrdenDaoImpl.contador (iso +
// contador de al menos 2 cifras). Sin iso conocido se incrementan las 2 últimas cifras.
function incrementarIsooper(isooper: string, iso?: string): string {
  const contador = contadorIsooper(isooper, iso);
  if (contador !== null) return formarIsooper(iso!, contador + 1);
  const match = isooper.match(/^(.*)(\d{2})$/);
  if (!match) return isooper;
  return formarIsooper(match[1], Number(match[2]) + 1);
}

function compare(a: any, b: any, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
